import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { useLang } from "@/context/LanguageContext";
import { scrollToId } from "@/lib/scroll";
import { IMAGES } from "@/constants/images";
import { EASE } from "./Reveal";

const BRANDS = ["Rudra Magnets", "EROWA", "SPREITZER"];

function CollageTile({ img, i, className = "" }) {
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.9, y: 18 }}
      animate={{ opacity: 1, scale: 1, y: 0 }}
      transition={{ duration: 0.8, delay: 0.5 + i * 0.09, ease: EASE }}
      className={`group relative overflow-hidden border border-white/10 bg-panel/40 ${className}`}
    >
      <img
        src={img.src}
        alt={img.alt}
        loading="lazy"
        className="h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-110"
        data-testid={`hero-collage-${i}`}
      />
      <div className="pointer-events-none absolute inset-0 bg-ink/10 transition-colors duration-500 group-hover:bg-transparent" />
    </motion.div>
  );
}

export default function Hero() {
  const { t } = useLang();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [0, 100]);
  const fade = useTransform(scrollYProgress, [0, 0.8], [1, 0.2]);

  return (
    <section id="top" ref={ref} className="bg-blueprint relative min-h-screen overflow-hidden pt-[72px]">
      <div className="pointer-events-none absolute -right-40 -top-40 h-[560px] w-[560px] rounded-full bg-crimson/15 blur-[140px]" />
      <div className="pointer-events-none absolute -left-40 bottom-0 h-[480px] w-[480px] rounded-full bg-steel/10 blur-[140px]" />

      <motion.div
        style={{ opacity: fade }}
        className="relative mx-auto grid max-w-7xl items-center gap-12 px-5 pb-16 pt-14 sm:px-8 lg:grid-cols-[1.1fr_0.9fr] lg:gap-16 lg:pt-20"
      >
        <div>
          <motion.div
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.15 }}
            className="flex items-center gap-3 font-mono text-[11px] uppercase tracking-[0.22em] text-steel sm:text-xs"
          >
            <span className="inline-block h-2 w-2 bg-crimson" />
            {t.hero.eyebrow}
          </motion.div>

          <h1 className="mt-6 font-display text-[clamp(3.2rem,9vw,7rem)] font-bold uppercase leading-[0.95] text-white">
            {t.hero.titleLines.map((line, i) => (
              <span key={i} className="block overflow-hidden pb-1">
                <motion.span
                  className={`block ${i === 1 ? "text-crimson" : ""}`}
                  initial={{ y: "112%" }}
                  animate={{ y: 0 }}
                  transition={{ duration: 0.95, delay: 0.3 + i * 0.13, ease: EASE }}
                >
                  {line}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.div
            initial={{ scaleX: 0 }}
            animate={{ scaleX: 1 }}
            transition={{ duration: 1.1, delay: 0.75, ease: EASE }}
            className="mt-8 h-[3px] w-40 origin-left bg-crimson"
          />

          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.9 }}
            className="mt-6 max-w-xl text-base leading-relaxed text-slate-300 sm:text-lg"
          >
            {t.hero.sub}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 1.05 }}
            className="mt-9 flex flex-wrap items-center gap-4"
          >
            <button
              data-testid="hero-cta-quote"
              onClick={() => scrollToId("#contact")}
              className="group inline-flex items-center gap-3 rounded-full bg-crimson px-7 py-3.5 text-sm font-semibold text-white transition-all hover:gap-4 hover:bg-crimson-hover"
            >
              {t.hero.ctaPrimary}
              <i className="fa-solid fa-arrow-right" />
            </button>
            <button
              data-testid="hero-cta-brands"
              onClick={() => scrollToId("#brands")}
              className="inline-flex items-center gap-3 rounded-full border border-white/20 px-7 py-3.5 text-sm font-semibold text-white transition-colors hover:border-white/50"
            >
              {t.hero.ctaSecondary}
            </button>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.25, duration: 0.8 }}
            className="mt-10 flex flex-wrap gap-2.5"
          >
            {BRANDS.map((b) => (
              <button
                key={b}
                data-testid={`hero-brand-pill-${b.toLowerCase().replace(/\s/g, "-")}`}
                onClick={() => scrollToId("#brands")}
                className="rounded-full border border-white/10 bg-white/[0.03] px-4 py-1.5 font-mono text-[11px] uppercase tracking-[0.14em] text-slate-300 transition-colors hover:border-steel/60 hover:text-steel"
              >
                {b}
              </button>
            ))}
          </motion.div>
        </div>

        <motion.div
          style={{ y }}
          initial={{ opacity: 0, scale: 0.96 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1, delay: 0.5, ease: EASE }}
          className="relative hidden md:block"
        >
          <div className="clip-frame relative border border-white/10 bg-panel/40 p-2.5">
            <div className="flex h-[520px] gap-2.5 lg:h-[600px]">
              <div className="flex flex-1 flex-col gap-2.5">
                <CollageTile img={IMAGES.heroCollage[0]} i={0} className="flex-[3]" />
                <CollageTile img={IMAGES.heroCollage[1]} i={1} className="flex-[2]" />
                <CollageTile img={IMAGES.heroCollage[2]} i={2} className="flex-[2]" />
              </div>
              <div className="flex flex-1 flex-col gap-2.5">
                <CollageTile img={IMAGES.heroCollage[3]} i={3} className="flex-[2]" />
                <CollageTile img={IMAGES.heroCollage[4]} i={4} className="flex-[3]" />
                <CollageTile img={IMAGES.heroCollage[5]} i={5} className="flex-[2]" />
              </div>
            </div>
            <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-ink/70 via-transparent to-transparent" />
          </div>

          <div
            className="clip-frame-sm absolute -left-8 top-10 border border-white/15 bg-ink/90 px-5 py-4 backdrop-blur"
            data-testid="hero-badge-years"
          >
            <div className="font-display text-4xl font-bold leading-none text-white">
              10<span className="text-crimson">+</span>
            </div>
            <div className="mt-1 max-w-[130px] text-[11px] leading-snug text-slate-400">
              {t.hero.badgeLabel}
            </div>
          </div>

          <motion.div
            animate={{ y: [0, -8, 0] }}
            transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
            className="absolute -right-4 top-1/3 border border-white/15 bg-ink/85 px-4 py-2.5 font-mono text-[11px] text-steel backdrop-blur"
            data-testid="hero-chip-0"
          >
            <i className="fa-solid fa-crosshairs mr-2" />
            {t.hero.chips[0]}
          </motion.div>

          <motion.div
            animate={{ y: [0, 8, 0] }}
            transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
            className="absolute -bottom-5 right-8 border border-white/15 bg-ink/85 px-4 py-2.5 font-mono text-[11px] text-slate-300 backdrop-blur"
            data-testid="hero-chip-1"
          >
            <i className="fa-solid fa-magnet mr-2 text-crimson" />
            {t.hero.chips[1]}
          </motion.div>
        </motion.div>
      </motion.div>

      <div className="-mt-6 px-5 pb-14 md:hidden">
        <div className="clip-frame relative grid grid-cols-2 gap-2 border border-white/10 bg-panel/40 p-2">
          {IMAGES.heroCollage.map((img, i) => (
            <img
              key={i}
              src={img.src}
              alt={img.alt}
              loading="lazy"
              className="h-24 w-full object-cover"
              data-testid={`hero-collage-mobile-${i}`}
            />
          ))}
          <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-ink/70 via-transparent to-transparent" />
          <div className="absolute bottom-3 left-3 font-mono text-[11px] text-steel">
            <i className="fa-solid fa-crosshairs mr-2" />
            {t.hero.chips[0]}
          </div>
        </div>
      </div>

      <motion.button
        data-testid="hero-scroll-hint"
        onClick={() => scrollToId("#about")}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.6 }}
        className="absolute bottom-6 left-1/2 hidden -translate-x-1/2 flex-col items-center gap-2 text-slate-500 transition-colors hover:text-white lg:flex"
      >
        <span className="font-mono text-[10px] uppercase tracking-[0.3em]">{t.hero.scroll}</span>
        <i className="fa-solid fa-chevron-down animate-bounce" />
      </motion.button>
    </section>
  );
}
